<script>
import OrdersAPI from 'dashboard/api/orders';

export default {
  name: 'OrderDetails',
  props: {
    orderId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      order: null,
    };
  },
  created() {
    this.fetchOrder();
  },
  methods: {
    async fetchOrder() {
      this.loading = true;
      try {
        const { data } = await OrdersAPI.show(this.orderId);
        this.order = data;
      } catch (error) {
        this.$toast.error(error.message || 'Error loading order');
      } finally {
        this.loading = false;
      }
    },
    statusColor(status) {
      const mapping = {
        pending: 'gray',
        processing: 'blue',
        shipped: 'orange',
        delivered: 'green',
        cancelled: 'red',
        refunded: 'red',
      };
      return mapping[status] || 'gray';
    },
    convertToHumanReadableDate(ts) {
      if (!ts) return '-';
      const date = new Date(ts * 1000);
      return date.toLocaleString();
    },
    formatPrice(cents, currency = 'USD') {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    },
    goBack() {
      this.$router.push({ name: 'orders_list' });
    },
    hasDiscount() {
      return (
        this.order && this.order.discount_cents && this.order.discount_cents > 0
      );
    },
    calculateSubtotal() {
      if (!this.order) return 0;
      return (
        this.order.total_cents -
        this.order.shipping_cents -
        this.order.tax_cents +
        (this.order.discount_cents || 0)
      );
    },
    openOrderUrl() {
      if (this.order?.order_url) {
        window.open(this.order.order_url, '_blank');
      }
    },
    statusColorClass(state) {
      switch ((state || '').toLowerCase()) {
        case 'delivered':
          return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200';
        case 'in transit':
          return 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200';
        case 'shipped':
          return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200';
        case 'pending':
          return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
        case 'out for delivery':
          return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200';
        case 'failed':
          return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      }
    },
    statusIcon(state) {
      switch ((state || '').toLowerCase()) {
        case 'delivered':
          return 'checkmark';
        case 'in transit':
          return 'vehicle-truck';
        case 'shipped':
          return 'send';
        case 'pending':
          return 'clock';
        case 'out for delivery':
          return 'location';
        case 'failed':
          return 'error-circle';
        default:
          return 'info';
      }
    },
    fulfillmentStatusColor(status) {
      const mapping = {
        pending:
          'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
        in_transit:
          'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200',
        delivered:
          'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200',
      };
      return (
        mapping[status] ||
        'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
      );
    },
    getTrackingStepStatus(step, shipment, fulfillment) {
      // Logic để xác định trạng thái của từng step
      const isShipped = shipment?.is_shipped || fulfillment?.was_shipped;
      const isInTransit =
        shipment?.is_in_transit ||
        fulfillment?.tracking_status === 'in_transit';
      const isOutForDelivery = shipment?.is_out_for_delivery;
      const isDelivered =
        shipment?.is_delivered || fulfillment?.tracking_status === 'delivered';

      switch (step) {
        case 'shipped':
          return {
            isActive: isShipped,
            isCompleted: isShipped,
            icon: isShipped ? 'checkmark' : 'send',
            color: isShipped
              ? 'bg-green-500 text-white'
              : 'bg-slate-300 dark:bg-slate-600 text-slate-500',
            textColor: isShipped
              ? 'text-green-700 dark:text-green-300'
              : 'text-slate-500 dark:text-slate-400',
            badge: isShipped
              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
              : null,
          };
        case 'in_transit':
          return {
            isActive: isInTransit,
            isCompleted: isDelivered, // Nếu đã giao thì step này cũng completed
            icon: isInTransit
              ? 'checkmark'
              : isDelivered
              ? 'checkmark'
              : 'vehicle-truck',
            color: isInTransit || isDelivered
              ? 'bg-blue-500 text-white'
              : 'bg-slate-300 dark:bg-slate-600 text-slate-500',
            textColor: isInTransit || isDelivered
              ? 'text-blue-700 dark:text-blue-300'
              : 'text-slate-500 dark:text-slate-400',
            badge: isInTransit
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
              : null,
          };
        case 'out_for_delivery':
          return {
            isActive: isOutForDelivery,
            isCompleted: isDelivered,
            icon: isOutForDelivery || isDelivered ? 'checkmark' : 'location',
            color: isOutForDelivery || isDelivered
              ? 'bg-yellow-500 text-white'
              : 'bg-slate-300 dark:bg-slate-600 text-slate-500',
            textColor: isOutForDelivery || isDelivered
              ? 'text-yellow-700 dark:text-yellow-300'
              : 'text-slate-500 dark:text-slate-400',
            badge: isOutForDelivery
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
              : null,
          };
        case 'delivered':
          return {
            isActive: isDelivered,
            isCompleted: isDelivered,
            icon: isDelivered ? 'checkmark' : 'home',
            color: isDelivered
              ? 'bg-green-500 text-white'
              : 'bg-slate-300 dark:bg-slate-600 text-slate-500',
            textColor: isDelivered
              ? 'text-green-700 dark:text-green-300'
              : 'text-slate-500 dark:text-slate-400',
            badge: isDelivered
              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
              : null,
          };
        default:
          return {
            isActive: false,
            isCompleted: false,
            icon: 'info',
            color: 'bg-slate-300 dark:bg-slate-600 text-slate-500',
            textColor: 'text-slate-500 dark:text-slate-400',
            badge: null,
          };
      }
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <div class="flex flex-col w-full h-full p-6 sm:py-8 lg:px-16 pb-12">
      <!-- Header -->
      <div
        class="flex items-center gap-4 mb-6 pb-6 border-b border-slate-200 dark:border-slate-700"
      >
        <woot-button
          variant="clear"
          color-scheme="secondary"
          icon="arrow-left"
          @click="goBack"
        >
          {{ $t('BACK_TO_ORDERS') }}
        </woot-button>

        <div v-if="order" class="flex-1">
          <div class="flex items-center gap-3">
            <h1
              class="text-2xl font-semibold text-slate-900 dark:text-slate-100"
            >
              #{{ order.external_order_id }}
            </h1>
            <span
              class="inline-flex px-3 py-1 text-sm font-semibold rounded-full"
              :class="{
                'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200':
                  statusColor(order.status) === 'gray',
                'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200':
                  statusColor(order.status) === 'blue',
                'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200':
                  statusColor(order.status) === 'orange',
                'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200':
                  statusColor(order.status) === 'green',
                'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200':
                  statusColor(order.status) === 'red',
              }"
            >
              {{ order.status }}
            </span>
            <span
              v-if="order.is_gift"
              class="inline-flex items-center px-2 py-1 text-sm font-medium bg-pink-100 text-pink-800 dark:bg-pink-800 dark:text-pink-200 rounded-full"
            >
              <fluent-icon icon="gift" size="14" class="mr-1" />
              {{ $t('GIFT_ORDER') }}
            </span>
          </div>
          <div class="flex items-center gap-4 mt-2">
            <p class="text-slate-600 dark:text-slate-400">
              {{ $t('PLACED_AT') }}:
              {{ convertToHumanReadableDate(order.placed_at) }}
            </p>
            <woot-button
              v-if="order.order_url"
              variant="clear"
              size="small"
              icon="open"
              @click="openOrderUrl"
            >
              {{ $t('VIEW_ON_PLATFORM') }}
            </woot-button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <woot-spinner size="" />
      </div>

      <!-- Order Details -->
      <div v-else-if="order" class="space-y-8 pb-8">
        <!-- Gift Message & Notes -->
        <div
          v-if="order.gift_message || order.note_from_buyer"
          class="grid grid-cols-1 xl:grid-cols-2 gap-6"
        >
          <!-- Gift Message -->
          <div
            v-if="order.gift_message"
            class="bg-pink-50 dark:bg-pink-900/20 p-6 rounded-xl border border-pink-200 dark:border-pink-800 shadow-sm"
          >
            <div class="flex items-center gap-3 mb-4">
              <div
                class="flex items-center justify-center w-8 h-8 bg-pink-100 dark:bg-pink-800 rounded-full"
              >
                <fluent-icon
                  icon="gift"
                  size="16"
                  class="text-pink-600 dark:text-pink-400"
                />
              </div>
              <h4
                class="text-base font-semibold text-pink-800 dark:text-pink-200"
              >
                {{ $t('GIFT_MESSAGE') }}
              </h4>
            </div>
            <div
              class="bg-white dark:bg-pink-950/50 p-4 rounded-lg border border-pink-100 dark:border-pink-700"
            >
              <p
                class="text-sm leading-relaxed text-pink-700 dark:text-pink-300"
              >
                {{ order.gift_message }}
              </p>
            </div>
          </div>

          <!-- Note from Buyer -->
          <div
            v-if="order.note_from_buyer"
            class="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800 shadow-sm"
          >
            <div class="flex items-center gap-3 mb-4">
              <div
                class="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full"
              >
                <fluent-icon
                  icon="chat"
                  size="16"
                  class="text-blue-600 dark:text-blue-400"
                />
              </div>
              <h4
                class="text-base font-semibold text-blue-800 dark:text-blue-200"
              >
                {{ $t('NOTE_FROM_BUYER') }}
              </h4>
            </div>
            <div
              class="bg-white dark:bg-blue-950/50 p-4 rounded-lg border border-blue-100 dark:border-blue-700"
            >
              <p
                class="text-sm leading-relaxed text-blue-700 dark:text-blue-300"
              >
                {{ order.note_from_buyer }}
              </p>
            </div>
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Order Summary -->
          <div
            class="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
            >
              {{ $t('SUMMARY') }}
            </h3>
            <div class="space-y-3">
              <div class="flex items-center gap-3">
                <fluent-icon icon="cart" size="16" class="text-slate-500" />
                <span
                  v-if="order.source"
                  class="text-sm text-slate-600 dark:text-slate-400 min-w-0 flex-shrink-0"
                >
                  {{ $t('SOURCE') }}:
                </span>
                <span
                  class="text-sm font-medium text-slate-900 dark:text-slate-100 capitalize min-w-0"
                >
                  {{ order.source }}
                </span>
              </div>
              <div
                v-if="order.shop_name || order.external_shop_id"
                class="flex items-center gap-3"
              >
                <fluent-icon
                  icon="building-shop"
                  size="16"
                  class="text-slate-500"
                />
                <span
                  class="text-sm text-slate-600 dark:text-slate-400 min-w-0 flex-shrink-0"
                >
                  {{ $t('SHOP') }}:
                </span>
                <span
                  class="text-sm font-medium text-slate-900 dark:text-slate-100 min-w-0"
                >
                  {{ order.shop_name || order.external_shop_id }}
                </span>
              </div>
              <div class="flex items-center gap-3">
                <fluent-icon icon="money" size="16" class="text-slate-500" />
                <span
                  v-if="order.total_cents"
                  class="text-sm text-slate-600 dark:text-slate-400 min-w-0 flex-shrink-0"
                >
                  {{ $t('TOTAL') }}:
                </span>
                <span
                  class="text-sm font-medium text-slate-900 dark:text-slate-100 min-w-0"
                >
                  {{ formatPrice(order.total_cents, order.currency_code) }}
                </span>
              </div>
              <div class="flex items-center gap-3">
                <fluent-icon icon="calendar" size="16" class="text-slate-500" />
                <span
                  v-if="order.paid_at"
                  class="text-sm text-slate-600 dark:text-slate-400 min-w-0 flex-shrink-0"
                >
                  {{ $t('PAID_AT') }}:
                </span>
                <span
                  v-if="order.paid_at"
                  class="text-sm text-slate-900 dark:text-slate-100 min-w-0"
                >
                  {{ convertToHumanReadableDate(order.paid_at) }}
                </span>
              </div>
              <div class="flex items-center gap-3">
                <fluent-icon
                  icon="checkmark-circle"
                  size="16"
                  class="text-slate-500"
                />
                <span
                  class="text-sm text-slate-600 dark:text-slate-400 min-w-0 flex-shrink-0"
                >
                  {{ $t('FULFILLED_AT') }}:
                </span>
                <span
                  class="text-sm text-slate-900 dark:text-slate-100 min-w-0"
                >
                  {{ convertToHumanReadableDate(order.fulfilled_at) || '-' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Buyer Information -->
          <div
            class="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
            >
              {{ $t('BUYER') }}
            </h3>
            <div class="space-y-3">
              <div class="flex items-center gap-3">
                <fluent-icon icon="person" size="16" class="text-slate-500" />
                <span
                  class="text-sm font-medium text-slate-900 dark:text-slate-100 min-w-0"
                >
                  {{ order.buyer_name || '-' }}
                </span>
              </div>
              <div class="flex items-center gap-3">
                <fluent-icon icon="mail" size="16" class="text-slate-500" />
                <span
                  v-if="order.buyer_email"
                  class="text-sm text-slate-600 dark:text-slate-400 min-w-0 break-all"
                >
                  {{ order.buyer_email || '-' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Payment Breakdown -->
          <div
            class="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4"
            >
              {{ $t('PAYMENT_BREAKDOWN') }}
            </h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-sm text-slate-600 dark:text-slate-400">
                  {{ $t('SUBTOTAL') }}:
                </span>
                <span class="text-sm text-slate-900 dark:text-slate-100">
                  {{ formatPrice(calculateSubtotal(), order.currency_code) }}
                </span>
              </div>
              <div
                v-if="hasDiscount()"
                class="flex justify-between items-center"
              >
                <span class="text-sm text-slate-600 dark:text-slate-400">
                  {{ $t('DISCOUNT') }}:
                </span>
                <span class="text-sm text-red-600 dark:text-red-400">
                  -{{ formatPrice(order.discount_cents, order.currency_code) }}
                </span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-slate-600 dark:text-slate-400">
                  {{ $t('SHIPPING') }}:
                </span>
                <span class="text-sm text-slate-900 dark:text-slate-100">
                  {{ formatPrice(order.shipping_cents, order.currency_code) }}
                </span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-slate-600 dark:text-slate-400">
                  {{ $t('TAX') }}:
                </span>
                <span class="text-sm text-slate-900 dark:text-slate-100">
                  {{ formatPrice(order.tax_cents, order.currency_code) }}
                </span>
              </div>
              <div
                class="flex justify-between items-center pt-3 border-t border-slate-200 dark:border-slate-600"
              >
                <span
                  class="text-sm font-semibold text-slate-900 dark:text-slate-100"
                >
                  {{ $t('TOTAL') }}:
                </span>
                <span
                  class="text-sm font-semibold text-slate-900 dark:text-slate-100"
                >
                  {{ formatPrice(order.total_cents, order.currency_code) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Items -->
        <div
          class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
        >
          <div
            class="px-6 py-4 border-b border-slate-200 dark:border-slate-700"
          >
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              {{ $t('ITEMS') }}
            </h3>
          </div>

          <div class="divide-y divide-slate-200 dark:divide-slate-700">
            <div v-for="item in order.items || []" :key="item.id" class="p-6">
              <div class="flex flex-col md:flex-row gap-4">
                <!-- Product Image -->
                <div class="flex-shrink-0">
                  <img
                    v-if="item.image_url"
                    :src="item.image_url"
                    :alt="item.title"
                    class="w-20 h-20 rounded-lg object-cover border border-slate-200 dark:border-slate-700"
                  />
                  <div
                    v-else
                    class="w-20 h-20 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center"
                  >
                    <fluent-icon
                      icon="image"
                      size="24"
                      class="text-slate-400"
                    />
                  </div>
                </div>

                <!-- Product Details -->
                <div class="flex-1 min-w-0">
                  <div
                    class="flex flex-col lg:flex-row lg:justify-between gap-4"
                  >
                    <div class="flex-1 min-w-0">
                      <h4
                        class="text-base font-medium text-slate-900 dark:text-slate-100 line-clamp-2 mb-2"
                      >
                        {{ item.title }}
                      </h4>
                      <div class="space-y-2 mb-4">
                        <div class="text-sm text-slate-500 dark:text-slate-400">
                          <span class="font-medium">{{ $t('SKU') }}:</span>
                          {{ item.product_identifier || '-' }}
                        </div>
                        <div
                          v-if="item.listing_id"
                          class="text-sm text-slate-500 dark:text-slate-400"
                        >
                          <span class="font-medium">
                            {{ $t('LISTING_ID') }}:
                          </span>
                          {{ item.listing_id }}
                        </div>
                      </div>

                      <!-- Product Tags -->
                      <div class="flex flex-wrap gap-2 mb-4">
                        <span
                          v-if="item.is_personalizable"
                          class="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200 rounded-full"
                        >
                          {{ $t('PERSONALIZABLE') }}
                        </span>
                        <span
                          v-if="item.is_digital"
                          class="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full"
                        >
                          {{ $t('DIGITAL') }}
                        </span>
                      </div>

                      <!-- Variations -->
                      <div
                        v-if="item.variations && item.variations.length"
                        class="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700"
                      >
                        <h5
                          class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3"
                        >
                          {{ $t('VARIATIONS') }}:
                        </h5>
                        <div
                          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                        >
                          <div
                            v-for="variation in item.variations"
                            :key="variation.variation_id"
                            class="bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-600"
                          >
                            <div
                              class="text-xs text-slate-500 dark:text-slate-400 mb-1"
                            >
                              {{ variation.name }}
                            </div>
                            <div
                              class="text-sm font-medium text-slate-900 dark:text-slate-100"
                            >
                              {{ variation.value }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Price & Quantity -->
                    <div class="text-right lg:ml-6 lg:min-w-[120px]">
                      <div
                        class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1"
                      >
                        {{ formatPrice(item.price_cents, item.currency_code) }}
                      </div>
                      <div
                        class="text-sm text-slate-600 dark:text-slate-400 mb-2"
                      >
                        {{ $t('QTY') }}: {{ item.quantity }}
                      </div>
                      <div
                        v-if="
                          item.usd_price_cents &&
                          item.usd_price_cents !== item.price_cents
                        "
                        class="text-xs text-slate-500"
                      >
                        ({{ formatPrice(item.usd_price_cents, 'USD') }})
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Shipments Information -->
        <div
          v-if="(order.shipments && order.shipments.length === 1) || ((!order.shipments || order.shipments.length === 0) && (order.fulfillments && order.fulfillments.length > 0))"
          class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8"
        >

          <div
            class="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <div class="flex items-center gap-2 mb-4 flex-wrap">
              <span class="font-semibold text-slate-900 dark:text-slate-100">
                #{{ order.shipments?.[0]?.shipment_id || order.fulfillments?.[0]?.fulfillment_id || 'N/A' }}
              </span>
              <span
                v-if="order.shipments?.[0]?.major_tracking_state"
                :class="[
                  'inline-flex items-center px-2 py-1 text-xs font-medium rounded-full',
                  statusColorClass(order.shipments?.[0]?.major_tracking_state),
                ]"
              >
                <fluent-icon
                  :icon="statusIcon(order.shipments?.[0]?.major_tracking_state)"
                  size="13"
                  class="mr-1"
                />
                {{ $t(order.shipments?.[0]?.major_tracking_state?.replace(/ /g, '_').toUpperCase()) }}
              </span>
              <span
                v-if="order.shipments?.[0]?.is_international"
                class="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full"
              >
                <fluent-icon icon="globe" size="12" class="mr-1" />
                {{ $t('INTERNATIONAL') }}
              </span>
              <span
                v-if="order.shipments?.[0]?.is_future_shipment"
                class="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-full"
              >
                <fluent-icon icon="clock" size="12" class="mr-1" />
                {{ $t('FUTURE_SHIPMENT') }}
              </span>
              <a
                v-if="order.shipments?.[0]?.tracking_url"
                :href="order.shipments[0].tracking_url"
                target="_blank"
                rel="noopener noreferrer"
                class="ml-2 text-xs text-blue-600 underline hover:text-blue-800 flex items-center gap-1"
              >
                <fluent-icon icon="link" size="13" />
                {{ $t('TRACKING_LINK') }}
              </a>
            </div>

            <!-- Carrier & Tracking Info -->
            <div class="grid grid-cols-1 gap-4 mb-4">
              <div class="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                <div class="flex items-center gap-2 mb-3">
                  <fluent-icon icon="vehicle-truck" size="16" class="text-slate-500" />
                  <span class="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {{ $t('CARRIER_INFO') }}
                  </span>
                </div>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-xs text-slate-500">{{ $t('CARRIER') }}:</span>
                    <span class="text-xs font-medium text-slate-900 dark:text-slate-100">
                      {{ order.shipments?.[0]?.carrier_name || order.fulfillments?.[0]?.shipping_carrier || '-' }}
                    </span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-xs text-slate-500">{{ $t('TRACKING_CODE') }}:</span>
                    <span class="text-xs font-medium text-slate-900 dark:text-slate-100">
                      {{ order.shipments?.[0]?.tracking_code || order.fulfillments?.[0]?.tracking_number || '-' }}
                    </span>
                  </div>
                  <div v-if="order.shipments?.[0]?.carrier_url" class="flex justify-between">
                    <span class="text-xs text-slate-500">{{ $t('CARRIER_URL') }}:</span>
                    <a
                      :href="order.shipments?.[0]?.carrier_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      {{ $t('VIEW') }}
                    </a>
                  </div>
                  <div v-if="order.fulfillments[0]?.shipping_method" class="flex justify-between">
                    <span class="text-xs text-slate-500">{{ $t('METHOD') }}:</span>
                    <span class="text-xs font-medium text-slate-900 dark:text-slate-100">
                      {{ order.fulfillments[0].shipping_method }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Tracking Flow -->
              <div
                class="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <div class="flex items-center gap-2 mb-4">
                  <fluent-icon
                    icon="flow"
                    size="16"
                    class="text-slate-500"
                  />
                  <span
                    class="text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    {{ $t('TRACKING_FLOW') }}
                  </span>
                </div>

                <!-- Flow Steps -->
                <div class="relative">
                  <!-- Flow Line -->
                  <div
                    class="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700"
                  />

                                    <!-- Step 1: Shipped -->
                  <div class="relative flex items-start gap-4 mb-4">
                    <div
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                    >
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center"
                        :class="getTrackingStepStatus('shipped', order.shipments?.[0], order.fulfillments?.[0]).color"
                      >
                        <fluent-icon
                          :icon="getTrackingStepStatus('shipped', order.shipments?.[0], order.fulfillments?.[0]).icon"
                          size="12"
                        />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span
                          class="text-sm font-medium"
                          :class="getTrackingStepStatus('shipped', order.shipments?.[0], order.fulfillments?.[0]).textColor"
                        >
                          {{ $t('SHIPPED') }}
                        </span>
                        <span
                          v-if="getTrackingStepStatus('shipped', order.shipments?.[0], order.fulfillments?.[0]).badge"
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
                                                      :class="getTrackingStepStatus('shipped', order.shipments?.[0], order.fulfillments?.[0]).badge"
                        >
                          {{ $t('COMPLETED') }}
                        </span>
                      </div>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ $t('SHIPPED_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>

                  <!-- Step 2: In Transit -->
                  <div class="relative flex items-start gap-4 mb-4">
                    <div
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                    >
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center"
                        :class="
                          order.shipments?.[0]?.is_in_transit ||
                          order.fulfillments?.[0]?.tracking_status === 'in_transit'
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-300 dark:bg-slate-600 text-slate-500'
                        "
                      >
                        <fluent-icon
                          :icon="
                            order.shipments?.[0]?.is_in_transit ||
                            order.fulfillments?.[0]?.tracking_status === 'in_transit'
                              ? 'checkmark'
                              : 'vehicle-truck'
                          "
                          size="12"
                        />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                      <span
                          class="text-sm font-medium"
                                                    :class="
                            order.shipments?.[0]?.is_in_transit ||
                            order.fulfillments?.[0]?.tracking_status === 'in_transit'
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-slate-500 dark:text-slate-400'
                          "
                        >
                          {{ $t('IN_TRANSIT') }}
                        </span>
                        <span
                          v-if="
                            order.shipments?.[0]?.is_in_transit ||
                            order.fulfillments?.[0]?.tracking_status === 'in_transit'
                          "
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full"
                        >
                          {{ $t('ACTIVE') }}
                      </span>
                    </div>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ $t('IN_TRANSIT_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>

                  <!-- Step 3: Out for Delivery -->
                  <div class="relative flex items-start gap-4 mb-4">
                    <div
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                    >
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center"
                        :class="
                          order.shipments?.[0]?.is_out_for_delivery
                            ? 'bg-yellow-500 text-white'
                            : 'bg-slate-300 dark:bg-slate-600 text-slate-500'
                        "
                      >
                        <fluent-icon
                          :icon="
                            order.shipments?.[0]?.is_out_for_delivery
                              ? 'checkmark'
                              : 'location'
                          "
                          size="12"
                        />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                      <span
                          class="text-sm font-medium"
                                                    :class="
                            order.shipments?.[0]?.is_out_for_delivery
                              ? 'text-yellow-700 dark:text-yellow-300'
                              : 'text-slate-500 dark:text-slate-400'
                          "
                        >
                          {{ $t('OUT_FOR_DELIVERY') }}
                        </span>
                        <span
                          v-if="order.shipments?.[0]?.is_out_for_delivery"
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200 rounded-full"
                      >
                          {{ $t('ACTIVE') }}
                      </span>
                    </div>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ $t('OUT_FOR_DELIVERY_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>

                  <!-- Step 4: Delivered -->
                  <div class="relative flex items-start gap-4">
                    <div
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                    >
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center"
                        :class="
                          order.shipments?.[0]?.is_delivered ||
                          order.fulfillments?.[0]?.tracking_status === 'delivered'
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-300 dark:bg-slate-600 text-slate-500'
                        "
                      >
                        <fluent-icon
                          :icon="
                            order.shipments?.[0]?.is_delivered ||
                            order.fulfillments?.[0]?.tracking_status === 'delivered'
                              ? 'checkmark'
                              : 'home'
                          "
                          size="12"
                        />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                      <span
                          class="text-sm font-medium"
                                                    :class="
                            order.shipments?.[0]?.is_delivered ||
                            order.fulfillments?.[0]?.tracking_status === 'delivered'
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-slate-500 dark:text-slate-400'
                          "
                        >
                          {{ $t('DELIVERED') }}
                        </span>
                        <span
                          v-if="
                            order.shipments?.[0]?.is_delivered ||
                            order.fulfillments?.[0]?.tracking_status === 'delivered'
                          "
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full"
                        >
                          {{ $t('COMPLETED') }}
                      </span>
                    </div>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ $t('DELIVERED_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Fulfillment Status Badges -->
                <div v-if="order.fulfillments[0]" class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div class="flex flex-wrap gap-2">
                      <span
                      v-if="order.fulfillments[0].is_complete"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full"
                      >
                      <fluent-icon icon="checkmark-circle" size="12" class="mr-1" />
                      {{ $t('COMPLETE') }}
                      </span>
                      <span
                      v-if="order.fulfillments[0].is_international"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200 rounded-full"
                    >
                      <fluent-icon icon="globe" size="12" class="mr-1" />
                      {{ $t('INTERNATIONAL') }}
                    </span>
                    <span
                      v-if="order.fulfillments[0].delivery_on_time"
                      class="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full"
                    >
                      <fluent-icon icon="clock" size="12" class="mr-1" />
                      {{ $t('ON_TIME') }}
                      </span>
                  </div>
                    </div>
                  </div>
                </div>


          </div>

                            <!-- Timeline Card (1 shipment) -->
          <div v-if="order.shipments && order.shipments.length === 1" class="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm mt-6">
            <!-- Block: Địa chỉ & mốc thời gian -->
            <div class="pb-4 mb-4 border-b border-slate-200 dark:border-slate-700">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <fluent-icon icon="location" size="14" class="text-blue-500" />
                    <span class="text-xs text-slate-500">{{ $t('TO') }}</span>
                  </div>
                  <div class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ order.shipments?.[0]?.to_address?.name || order.fulfillments?.[0]?.to_address?.name }}</div>
                  <div class="text-xs text-slate-600 dark:text-slate-400">
                    {{ (order.shipments?.[0]?.to_address?.first_line || order.fulfillments?.[0]?.to_address?.first_line) }}
                    {{ (order.shipments?.[0]?.to_address?.city || order.fulfillments?.[0]?.to_address?.city) }}
                    {{ (order.shipments?.[0]?.to_address?.state || order.fulfillments?.[0]?.to_address?.state) }}
                    {{ (order.shipments?.[0]?.to_address?.zip || order.fulfillments?.[0]?.to_address?.zip) }}
                  </div>
                </div>
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <fluent-icon icon="location-arrow" size="14" class="text-green-500" />
                    <span class="text-xs text-slate-500">{{ $t('FROM') }}</span>
                  </div>
                  <div class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ order.shipments?.[0]?.from_address?.name || order.fulfillments?.[0]?.from_address?.name }}</div>
                  <div class="text-xs text-slate-600 dark:text-slate-400">
                    {{ (order.shipments?.[0]?.from_address?.first_line || order.fulfillments?.[0]?.from_address?.first_line) }}
                    {{ (order.shipments?.[0]?.from_address?.city || order.fulfillments?.[0]?.from_address?.city) }}
                    {{ (order.shipments?.[0]?.from_address?.state || order.fulfillments?.[0]?.from_address?.state) }}
                    {{ (order.shipments?.[0]?.from_address?.zip || order.fulfillments?.[0]?.from_address?.zip) }}
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <fluent-icon icon="calendar" size="14" class="text-slate-500" />
                    <span class="text-xs text-slate-500">{{ $t('MAILING_DATE') }}</span>
                  </div>
                  <div class="text-sm text-slate-900 dark:text-slate-100 font-medium">
                    {{ convertToHumanReadableDate(order.shipments?.[0]?.mailing_date) }}
                  </div>
                </div>
                <div v-if="order.fulfillments[0]">
                  <div v-if="order.fulfillments[0].expected_ship_date" class="mb-1">
                    <div class="flex items-center gap-2">
                      <fluent-icon icon="calendar" size="14" class="text-slate-500" />
                      <span class="text-xs text-slate-500">{{ $t('EXPECTED_SHIP') }}</span>
                    </div>
                    <div class="text-sm text-slate-900 dark:text-slate-100 font-medium">
                      {{ convertToHumanReadableDate(order.fulfillments[0].expected_ship_date) }}
                    </div>
                  </div>
                  <div v-if="order.fulfillments[0].actual_ship_date" class="mb-1">
                    <div class="flex items-center gap-2">
                      <fluent-icon icon="calendar" size="14" class="text-slate-500" />
                      <span class="text-xs text-slate-500">{{ $t('ACTUAL_SHIP') }}</span>
                    </div>
                    <div class="text-sm text-slate-900 dark:text-slate-100 font-medium">
                      {{ convertToHumanReadableDate(order.fulfillments[0].actual_ship_date) }}
                    </div>
                  </div>
                  <div v-if="order.fulfillments[0].expected_delivery_date" class="mb-1">
                    <div class="flex items-center gap-2">
                      <fluent-icon icon="calendar" size="14" class="text-slate-500" />
                      <span class="text-xs text-slate-500">{{ $t('EXPECTED_DELIVERY') }}</span>
                    </div>
                    <div class="text-sm text-slate-900 dark:text-slate-100 font-medium">
                      {{ convertToHumanReadableDate(order.fulfillments[0].expected_delivery_date) }}
                    </div>
                  </div>
                  <div v-if="order.fulfillments[0].estimated_delivery_date">
                    <div class="flex items-center gap-2">
                      <fluent-icon icon="calendar" size="14" class="text-slate-500" />
                      <span class="text-xs text-slate-500">{{ $t('ESTIMATED_DELIVERY') }}</span>
                    </div>
                    <div class="text-sm text-slate-900 dark:text-slate-100 font-medium">
                      {{ order.fulfillments[0].estimated_delivery_date }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Timeline tracking event -->
            <div>
              <div class="text-xs text-slate-500 mb-1">{{ $t('TIMELINE') }}</div>
              <ul>
                <li
                  v-for="event in order.shipments?.[0]?.tracking_events || []"
                  :key="event.tracking_event_id || event.date"
                  class="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 py-1 border-b border-slate-100 dark:border-slate-700"
                >
                  <fluent-icon icon="location" size="12" class="text-blue-400" />
                  <span class="font-medium">
                    {{ convertToHumanReadableDate(event.date) }}
                  </span>
                  -
                  <span>{{ event.location }}</span>
                  -
                  <span>{{ event.event_type }}</span>
                </li>
              </ul>
            </div>
          </div>


        </div>

        <div
          v-else-if="order.shipments && order.shipments.length > 1"
          class="space-y-6 mt-8"
        >
          <div
            v-for="shipment in order.shipments"
            :key="shipment.id || shipment.shipment_id"
            class="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <div class="flex items-center gap-2 mb-4 flex-wrap">
                            <span class="font-semibold text-slate-900 dark:text-slate-100">
                #{{ shipment?.shipment_id }}
              </span>
              <span
                v-if="shipment?.major_tracking_state"
                :class="`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${statusColorClass(shipment?.major_tracking_state)}`"
              >
                <fluent-icon
                  :icon="statusIcon(shipment?.major_tracking_state)"
                  size="13"
                  class="mr-1"
                />
                {{ $t(shipment?.major_tracking_state?.replace(/ /g, '_').toUpperCase()) }}
              </span>
              <span
                v-if="shipment?.is_international"
                class="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full"
              >
                <fluent-icon icon="globe" size="12" class="mr-1" />
                {{ $t('INTERNATIONAL') }}
              </span>
              <span
                v-if="shipment?.is_future_shipment"
                class="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-full"
              >
                <fluent-icon icon="clock" size="12" class="mr-1" />
                {{ $t('FUTURE_SHIPMENT') }}
              </span>
              <a
                v-if="shipment?.tracking_url"
                :href="shipment?.tracking_url"
                target="_blank"
                rel="noopener noreferrer"
                class="ml-2 text-xs text-blue-600 underline hover:text-blue-800 flex items-center gap-1"
              >
                <fluent-icon icon="link" size="13" />
                {{ $t('TRACKING_LINK') }}
              </a>
            </div>

            <!-- Carrier & Tracking Info -->
            <div class="grid grid-cols-1 gap-4 mb-4">
              <div class="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                <div class="flex items-center gap-2 mb-3">
                  <fluent-icon icon="vehicle-truck" size="16" class="text-slate-500" />
                  <span class="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {{ $t('CARRIER_INFO') }}
                      </span>
                    </div>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-xs text-slate-500">{{ $t('CARRIER') }}:</span>
                    <span class="text-xs font-medium text-slate-900 dark:text-slate-100">
                      {{ shipment?.carrier_name || '-' }}
                    </span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-xs text-slate-500">{{ $t('TRACKING_CODE') }}:</span>
                    <span class="text-xs font-medium text-slate-900 dark:text-slate-100">
                      {{ shipment?.tracking_code || '-' }}
                    </span>
                  </div>
                  <div v-if="shipment?.carrier_url" class="flex justify-between">
                    <span class="text-xs text-slate-500">{{ $t('CARRIER_URL') }}:</span>
                    <a
                      :href="shipment?.carrier_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      {{ $t('VIEW') }}
                    </a>
                  </div>
                </div>
              </div>

              <!-- Tracking Flow -->
              <div
                class="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <div class="flex items-center gap-2 mb-4">
                  <fluent-icon
                    icon="flow"
                    size="16"
                    class="text-slate-500"
                  />
                      <span
                    class="text-sm font-semibold text-slate-700 dark:text-slate-300"
                      >
                    {{ $t('TRACKING_FLOW') }}
                      </span>
                </div>

                <!-- Flow Steps -->
                <div class="relative">
                  <!-- Flow Line -->
                  <div
                    class="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700"
                  />

                  <!-- Step 1: Shipped -->
                  <div class="relative flex items-start gap-4 mb-4">
                    <div
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                    >
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center"
                        :class="
                          shipment?.is_shipped
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-300 dark:bg-slate-600 text-slate-500'
                        "
                      >
                        <fluent-icon
                          :icon="shipment?.is_shipped ? 'checkmark' : 'send'"
                          size="12"
                        />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span
                          class="text-sm font-medium"
                          :class="
                            shipment?.is_shipped
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-slate-500 dark:text-slate-400'
                          "
                        >
                          {{ $t('SHIPPED') }}
                        </span>
                        <span
                          v-if="shipment?.is_shipped"
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full"
                        >
                          {{ $t('COMPLETED') }}
                      </span>
                    </div>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ $t('SHIPPED_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>

                  <!-- Step 2: In Transit -->
                  <div class="relative flex items-start gap-4 mb-4">
                    <div
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                    >
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center"
                        :class="
                          shipment?.is_in_transit
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-300 dark:bg-slate-600 text-slate-500'
                        "
                      >
                        <fluent-icon
                          :icon="
                            shipment?.is_in_transit
                              ? 'checkmark'
                              : 'vehicle-truck'
                          "
                          size="12"
                        />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span
                          class="text-sm font-medium"
                          :class="
                            shipment?.is_in_transit
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-slate-500 dark:text-slate-400'
                          "
                        >
                          {{ $t('IN_TRANSIT') }}
                        </span>
                        <span
                          v-if="shipment?.is_in_transit"
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded-full"
                        >
                          {{ $t('ACTIVE') }}
                        </span>
                      </div>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ $t('IN_TRANSIT_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>

                  <!-- Step 3: Out for Delivery -->
                  <div class="relative flex items-start gap-4 mb-4">
                    <div
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                    >
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center"
                        :class="
                          shipment?.is_out_for_delivery
                            ? 'bg-yellow-500 text-white'
                            : 'bg-slate-300 dark:bg-slate-600 text-slate-500'
                        "
                      >
                        <fluent-icon
                          :icon="
                            shipment?.is_out_for_delivery
                              ? 'checkmark'
                              : 'location'
                          "
                          size="12"
                        />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span
                          class="text-sm font-medium"
                          :class="
                            shipment?.is_out_for_delivery
                              ? 'text-yellow-700 dark:text-yellow-300'
                              : 'text-slate-500 dark:text-slate-400'
                          "
                        >
                          {{ $t('OUT_FOR_DELIVERY') }}
                        </span>
                        <span
                          v-if="shipment?.is_out_for_delivery"
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200 rounded-full"
                        >
                          {{ $t('ACTIVE') }}
                        </span>
                      </div>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ $t('OUT_FOR_DELIVERY_DESCRIPTION') }}
                      </p>
                    </div>
                  </div>

                  <!-- Step 4: Delivered -->
                  <div class="relative flex items-start gap-4">
                    <div
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                    >
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center"
                        :class="
                          shipment?.is_delivered
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-300 dark:bg-slate-600 text-slate-500'
                        "
                      >
                        <fluent-icon
                          :icon="shipment?.is_delivered ? 'checkmark' : 'home'"
                          size="12"
                        />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span
                          class="text-sm font-medium"
                          :class="
                            shipment?.is_delivered
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-slate-500 dark:text-slate-400'
                          "
                        >
                          {{ $t('DELIVERED') }}
                        </span>
                        <span
                          v-if="shipment?.is_delivered"
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full"
                        >
                          {{ $t('COMPLETED') }}
                        </span>
                  </div>
                      <p class="text-xs text-slate-500 dark:text-slate-400">
                        {{ $t('DELIVERED_DESCRIPTION') }}
                      </p>
                </div>
              </div>
            </div>
          </div>
        </div>

            <!-- Block: Địa chỉ & mốc thời gian -->
            <div class="pb-4 mb-4 border-b border-slate-200 dark:border-slate-700">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <fluent-icon icon="location" size="14" class="text-blue-500" />
                    <span class="text-xs text-slate-500">{{ $t('TO') }}</span>
      </div>
                  <div class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {{ shipment?.to_address?.name }}
                  </div>
                  <div class="text-xs text-slate-600 dark:text-slate-400">
                    {{ shipment?.to_address?.first_line }}
                    {{ shipment?.to_address?.city }}
                    {{ shipment?.to_address?.state }}
                    {{ shipment?.to_address?.zip }}
                  </div>
                </div>
                <div v-if="shipment?.from_address">
                  <div class="flex items-center gap-2 mb-1">
                    <fluent-icon icon="location-arrow" size="14" class="text-green-500" />
                    <span class="text-xs text-slate-500">{{ $t('FROM') }}</span>
                  </div>
                  <div class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {{ shipment?.from_address?.name }}
                  </div>
                  <div class="text-xs text-slate-600 dark:text-slate-400">
                    {{ shipment?.from_address?.first_line }}
                    {{ shipment?.from_address?.city }}
                    {{ shipment?.from_address?.state }}
                    {{ shipment?.from_address?.zip }}
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <fluent-icon icon="calendar" size="14" class="text-slate-500" />
                    <span class="text-xs text-slate-500">{{ $t('MAILING_DATE') }}</span>
                  </div>
                  <div class="text-sm text-slate-900 dark:text-slate-100 font-medium">
                    {{ convertToHumanReadableDate(shipment?.mailing_date) }}
                  </div>
                  <div v-if="shipment?.note" class="mt-2">
                    <div class="text-xs text-slate-500 mb-1">{{ $t('NOTE') }}</div>
                    <div class="text-sm text-slate-900 dark:text-slate-100">
                      {{ shipment?.note }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Timeline tracking event -->
            <div v-if="shipment?.tracking_events && shipment?.tracking_events.length">
              <div class="text-xs text-slate-500 mb-1">{{ $t('TIMELINE') }}</div>
              <ul>
                <li
                  v-for="event in shipment?.tracking_events"
                  :key="event.tracking_event_id || event.date"
                  class="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 py-1 border-b border-slate-100 dark:border-slate-700"
                >
                  <fluent-icon icon="location" size="12" class="text-blue-400" />
                  <span class="font-medium">
                    {{ convertToHumanReadableDate(event.date) }}
                  </span>
                  -
                  <span>{{ event.location }}</span>
                  -
                  <span>{{ event.event_type }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
